/**
 * Log Viewer
 * Developer tool to view in-app logging
 */

import React, { useMemo } from 'react';
import {
  Stack,
  Text,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  type IColumn,
  DefaultButton,
  Dropdown,
  type IDropdownOption
} from '@fluentui/react';
import { useLogging } from '../contexts/LoggingContext';
import type { LogEntry } from '../types';
import { formatUtcAsLocalTime } from '../utils/dateTime';

const LogViewer: React.FC = () => {
  const { logs, clearLogs } = useLogging();
  const [filterLevel, setFilterLevel] = React.useState<string>('all');

  const levelOptions: IDropdownOption[] = [
    { key: 'all', text: 'All Levels' },
    { key: 'info', text: 'Info' },
    { key: 'warn', text: 'Warning' },
    { key: 'error', text: 'Error' },
    { key: 'debug', text: 'Debug' }
  ];

  const filteredLogs = useMemo(() => {
    if (filterLevel === 'all') return logs;
    return logs.filter(log => log.level === filterLevel);
  }, [logs, filterLevel]);

  const getLevelColor = (level: string): string => {
    switch (level) {
      case 'error': return '#d13438';
      case 'warn': return '#ff8c00';
      case 'info': return '#0078d4';
      case 'debug': return '#605e5c';
      default: return '#000000';
    }
  };

  const columns: IColumn[] = [
    {
      key: 'timestamp',
      name: 'Time',
      fieldName: 'timestamp',
      minWidth: 100,
      maxWidth: 120,
      onRender: (item: LogEntry) => (
        <Text variant="small" styles={{ root: { fontFamily: 'monospace', fontSize: 11 } }}>
          {formatUtcAsLocalTime(item.timestamp)}
        </Text>
      )
    },
    {
      key: 'level',
      name: 'Level',
      fieldName: 'level',
      minWidth: 60,
      maxWidth: 80,
      onRender: (item: LogEntry) => (
        <Text
          variant="small"
          styles={{
            root: {
              fontWeight: 600,
              color: getLevelColor(item.level),
              textTransform: 'uppercase',
              fontSize: 11
            }
          }}
        >
          {item.level}
        </Text>
      )
    },
    {
      key: 'message',
      name: 'Message',
      fieldName: 'message',
      minWidth: 300,
      isMultiline: true,
      onRender: (item: LogEntry) => (
        <Text variant="small" styles={{ root: { fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-word' } }}>
          {item.message}
        </Text>
      )
    },
    {
      key: 'data',
      name: 'Data',
      minWidth: 200,
      isMultiline: true,
      onRender: (item: LogEntry) => (
        item.data ? (
          <pre
            style={{
              fontFamily: 'monospace',
              fontSize: 10,
              margin: 0,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              color: '#323130',
              maxHeight: 200,
              overflow: 'auto',
              backgroundColor: '#f3f2f1',
              padding: 8,
              borderRadius: 2
            }}
          >
            {item.data}
          </pre>
        ) : (
          <Text variant="small" styles={{ root: { color: '#a19f9d', fontStyle: 'italic' } }}>
            (no data)
          </Text>
        )
      )
    }
  ];

  return (
    <Stack tokens={{ childrenGap: 16 }}>
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <Text variant="xxLarge" styles={{ root: { fontWeight: 600 } }}>
          Developer Logs
        </Text>
        <Stack horizontal tokens={{ childrenGap: 12 }}>
          <Dropdown
            options={levelOptions}
            selectedKey={filterLevel}
            onChange={(_, option) => option && setFilterLevel(option.key as string)}
            styles={{ root: { width: 150 } }}
          />
          <DefaultButton
            text="Clear Logs"
            iconProps={{ iconName: 'Delete' }}
            onClick={clearLogs}
          />
        </Stack>
      </Stack>

      <Text variant="medium" styles={{ root: { color: '#605e5c' } }}>
        In-app logging for debugging On-Call Manager operations. Most recent logs appear first.
      </Text>

      <Stack
        styles={{
          root: {
            backgroundColor: '#f3f2f1',
            padding: 12,
            borderRadius: 4,
            border: '1px solid #edebe9'
          }
        }}
      >
        <Text variant="small" styles={{ root: { fontFamily: 'monospace', fontSize: 11 } }}>
          Total logs: {logs.length} | Filtered: {filteredLogs.length} | Keeping last 100 entries
        </Text>
      </Stack>

      {filteredLogs.length === 0 ? (
        <Stack horizontalAlign="center" tokens={{ padding: 40 }}>
          <Text variant="large" styles={{ root: { color: '#a19f9d' } }}>
            {logs.length === 0 ? 'No logs recorded yet' : 'No logs match the selected filter'}
          </Text>
        </Stack>
      ) : (
        <DetailsList
          items={filteredLogs}
          columns={columns}
          layoutMode={DetailsListLayoutMode.justified}
          selectionMode={SelectionMode.none}
          isHeaderVisible={true}
          styles={{
            root: {
              '.ms-DetailsRow': {
                borderBottom: '1px solid #edebe9'
              },
              '.ms-DetailsRow:hover': {
                backgroundColor: '#f3f2f1'
              }
            }
          }}
        />
      )}
    </Stack>
  );
};

export default LogViewer;
